import { FileText } from 'lucide-react';
import { translations } from '../../lib/i18n/translations';
import type { VoiceCall } from '../../lib/voice/hooks';

type VoiceT = typeof translations.en.settings.voice;

interface Props {
  t: VoiceT;
  lang: string;
  call: VoiceCall;
  onView: () => void;
}

function statusColor(status: string): string {
  if (status === 'completed' || status === 'done') return 'text-[#0f7b6c] bg-[rgba(15,123,108,0.08)]';
  if (status === 'failed' || status === 'rejected') return 'text-[#e03e3e] bg-[rgba(224,62,62,0.06)]';
  if (status === 'dialing' || status === 'in_progress') return 'text-[#2383E2] bg-[rgba(35,131,226,0.08)]';
  return 'text-[#787774] bg-[rgba(55,53,47,0.06)]';
}

function fmtDuration(secs: number | null): string {
  if (!secs || secs <= 0) return '—';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export function VoiceCallRow({ t, lang, call, onView }: Props) {
  const dateFmt = new Intl.DateTimeFormat(lang === 'it' ? 'it-IT' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  const hasTranscript = !!call.full_transcript || !!call.ai_summary;

  return (
    <li className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 px-3 py-2.5 hover:bg-[#F7F6F3]">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium text-[#37352f] truncate">
            {call.meeting_title || t.calls.columnTitle}
          </span>
          {call.is_test && (
            <span className="text-[10px] uppercase tracking-wide text-[#cb912f] bg-[rgba(203,145,47,0.1)] px-1.5 py-px rounded">
              {t.calls.test}
            </span>
          )}
        </div>
        <div className="text-[11px] text-[#9b9a97] mt-0.5">{dateFmt.format(new Date(call.created_at))}</div>
      </div>

      <span className={`text-[11px] px-1.5 py-0.5 rounded ${statusColor(call.status)}`}>{call.status}</span>

      <div className="text-[12px] text-[#787774] tabular-nums text-right">
        <div>{fmtDuration(call.duration_seconds)}</div>
        {call.voice_cost_credits != null && (
          <div className="text-[11px] text-[#9b9a97]">
            {t.calls.credits.replace('{n}', String(call.voice_cost_credits))}
          </div>
        )}
      </div>

      <button
        onClick={onView}
        disabled={!hasTranscript}
        title={t.calls.viewTranscript}
        className="flex h-7 w-7 items-center justify-center rounded-md text-[#787774] hover:bg-[#EBEBEA] hover:text-[#37352f] disabled:opacity-30"
      >
        <FileText className="h-3.5 w-3.5" />
      </button>
    </li>
  );
}
